import React, { useEffect, useRef, useState } from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { CHECKOUT_URL } from './CTAButton';
import { PRICE } from '../data/catio';
import { trackInitiateCheckout } from '../utils/metaPixel';
import { withUtmParams } from '../utils/checkoutUrl';

/** Elements that make the bar redundant while they are on screen. */
const HIDE_WHEN_VISIBLE = ['offer-cta', 'final-cta', 'site-footer'];
const inView = (id: string) => {
  const el = document.getElementById(id);
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  return rect.top < window.innerHeight && rect.bottom > 0;
};

/**
 * Mobile-only purchase bar. Stays dormant until the visitor has actually seen
 * the offer price (#offer-price); after that it shows whenever no other buy
 * button (offer CTA, final CTA) or the footer is on screen.
 * GET IT NOW opens the real checkout — it never scrolls back to #offer.
 */
export function StickyBuyBar() {
  const [visible, setVisible] = useState(false);
  const seenPrice = useRef(false);
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      if (!seenPrice.current) {
        const price = document.getElementById('offer-price');
        if (price && price.getBoundingClientRect().top < window.innerHeight * 0.9) {
          seenPrice.current = true;
        }
      }
      setVisible(seenPrice.current && !HIDE_WHEN_VISIBLE.some(inView));
    };
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    window.addEventListener('resize', onScroll);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);
  return <div aria-hidden={!visible} className={`fixed inset-x-3 z-40 transition-[transform,opacity] duration-[250ms] md:hidden ${visible ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-[calc(100%+24px)] opacity-0'}`} style={{
    bottom: 'calc(12px + env(safe-area-inset-bottom))',
    transitionTimingFunction: 'cubic-bezier(0.23, 1, 0.32, 1)'
  }} data-id="element-336" data-mp="17rqghg5qabz2">
      <div className="flex items-center justify-between gap-3 rounded-[20px] bg-ink py-2 pl-4 pr-2 shadow-[0_20px_40px_-12px_rgba(0,0,0,0.6)] ring-1 ring-white/10" data-id="element-337" data-mp="2k0zjxls23jpd">
        <div className="min-w-0 leading-none" data-id="element-338" data-mp="2xywholi8xw5m">
          <p className="wordmark text-[0.95rem] text-cream" data-id="element-339" data-mp="2t262e0e9n4l9">
            Catio <span className="text-ember-light" data-id="element-340" data-mp="2sztjnl0ooq75">Ready</span>™
          </p>
          <p className="mt-1 text-[1.3rem] font-extrabold tracking-[-0.02em] text-cream" data-id="element-341" data-mp="37q0qt4l8qmsj">
            {PRICE.amount}
          </p>
        </div>
        <a href={withUtmParams(CHECKOUT_URL)} onClick={event => {
        trackInitiateCheckout();
        event.currentTarget.href = withUtmParams(CHECKOUT_URL);
      }} tabIndex={visible ? 0 : -1} className="flex min-h-[48px] shrink-0 items-center gap-2 rounded-[14px] bg-ember-cta px-5 text-[0.84rem] font-bold uppercase tracking-[0.05em] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.45),inset_0_8px_14px_-8px_rgba(255,214,180,0.55),inset_0_-2px_0_rgba(0,0,0,0.12)] transition-colors duration-150 ease-out hover:bg-ember-deep active:bg-ember-deep focus:outline-none focus-visible:ring-4 focus-visible:ring-ember/40" data-id="element-342" data-mp="3gqyzw1ajdkhz">
          Get it now
          <ArrowRightIcon className="h-4 w-4" strokeWidth={2.5} aria-hidden data-id="element-343" data-mp="3heoacudd5gro" />
        </a>
      </div>
    </div>;
}